import { useMutation } from "@tanstack/react-query";
import { Button, Checkbox, Form, Image, Input, message, Modal } from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { motion } from "framer-motion";
import moment from "moment";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import GroupBookingForm from "./GroupBookingForm";
import { COMPARE_TYPES } from "common";
import dayjs from "dayjs";
import { compareDate } from "common/datetime";

const { TextArea } = Input;

const StyleFormItem = styled(Form.Item)`
	.ant-form-item-label > label::after {
		visibility: visible !important;
	}
	.ant-form-item-label {
		padding-bottom: 4px !important;
	}
`;

const ReservationMember = (props) => {
	const { publicSettings, logo, accessToken, category, occasion, occurrence } =
		props;
	const [modalWarningEmpty, contextHolderModalWarningEmpty] = Modal.useModal();
	const { liffId, categoryId, occasionId } = useParams();
	const navigate = useNavigate();
	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const [reservationForm] = Form.useForm();
	const { search } = useLocation();
	const searchParams = new URLSearchParams(search);
	const messages = category?.categoryMessages || [];

	const reserveMutation = useMutation(API.CLIENT_RESERVE_OCCASION, {
		onSuccess: (response) => {
			if (isMountedRef.current) {
				if (response?.data?.registrationId) {
					navigate(
						`${COMMONS.CLIENT_REGISTRATION_DETAIL_ROUTE}/${liffId}/${response.data.registrationId}`,
					);
				} else {
					navigate(`${COMMONS.CLIENT_CATEGORIES_ROUTE}/${liffId}`);
				}

				window.scrollTo(0, 0);
			}
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_CONFLICT_ERROR) {
				Modal.warning({
					title: "時間帯が満員になりました。",
					content: COMMONS.WARN_RESERVATION_FULL_MSG,
					centered: true,
					okText: "確認",
					okButtonProps: {
						type: "primary",
					},
				});
			} else if (
				error?.response?.status === COMMONS.RESPONSE_BAD_REQUEST_ERROR
			) {
				message.error({
					content:
						typeof error?.response?.data === "string"
							? error?.response?.data
							: "時間の重複ですので、予約できません。",
					key: "時間の重複ですので、予約できません。",
				});
			} else if (
				error?.response?.status === COMMONS.RESPONSE_NOT_ACCEPTABLE_ERROR
			) {
				Modal.warning({
					title: "この時間帯を予約することはできません。",
					content: COMMONS.WARN_RESERVATION_PAST_MSG,
					centered: true,
					okText: "確認",
					okButtonProps: {
						type: "primary",
					},
				});
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	const reservationHandler = (data) => {
		if (
			(category?.groupBooking || occasion?.groupBooking) &&
			!data.participantCount
		) {
			return modalWarningEmpty.info({
				title: "確認",
				content: "申し訳ございませんが、空席でのご参加はできません",
				centered: true,
				okText: "確認",
			});
		}

		const isOccurrenceInPast = compareDate(
			occurrence?.startAt,
			dayjs(),
			COMPARE_TYPES.IS_BEFORE,
		);

		if (isOccurrenceInPast) {
			Modal.warning({
				title: "この時間帯を予約することはできません。",
				content: COMMONS.WARN_RESERVATION_PAST_MSG,
				centered: true,
				okText: "確認",
				okButtonProps: {
					type: "primary",
				},
			});
			return;
		}

		const isAvailable =
			COMMONS.GET_LEFT_SLOTS(
				occurrence?.maxAttendee || 0,
				occurrence?.maxExpected || 0,
			) >= 1;

		if (isAvailable) {
			const paramData = {
				occurrenceId: occurrence?.occurrenceId,
				message: data?.memo,
				note: category?.categoryMessages?.length > 0 ? data : null,
				accessToken: accessToken,
				...(category?.groupBooking || occasion?.groupBooking
					? {
							participantName: data.participantName,
							participantCount: data.participantCount,
							companionCount: data.companionCount,
						}
					: {}),
			};

			reserveMutation.mutate(paramData);
		} else {
			Modal.warning({
				title: "時間帯が満員になりました。",
				content: COMMONS.WARN_RESERVATION_FULL_MSG,
				centered: true,
				okText: "確認",
				okButtonProps: {
					type: "primary",
				},
			});
		}
	};

	return (
		<motion.div
			variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
			initial="hidden"
			animate="show"
			exit="hidden"
			className="my-8"
		>
			<motion.div
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				className="mb-8"
			>
				{logo ? (
					<Image
						preview={false}
						width={"100%"}
						style={{ maxHeight: "150px" }}
						className="w-full object-contain"
						src={`${API.SETTINGS_UPLOADS_URL}${logo}`}
						fallback="/no-image.png"
					/>
				) : (
					""
				)}
			</motion.div>
			<motion.div
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				className="mb-4"
			>
				<p
					className="px-4 py-2 text-center text-lg font-bold mt-8 mb-4 rounded"
					style={{
						backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
						color: publicSettings?.PRIMARY_COLOR,
					}}
				>
					{`ご希望の${COMMONS.DEFAULT_SYSTEM_TYPE}・${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム`}
				</p>
				<p className="text-black text-center text-lg font-bold">
					{category?.title || ""}
				</p>
				{occasion?.title && (
					<p className="text-center text-base font-bold">
						（{occasion.title}）
					</p>
				)}
			</motion.div>
			<motion.div
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				className="mb-8"
			>
				<p
					className="px-4 py-2 text-center text-lg font-bold mt-8 mb-4 rounded"
					style={{
						backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
						color: publicSettings?.PRIMARY_COLOR,
					}}
				>
					ご希望の時間帯
				</p>
				<div className="text-center">
					{category?.isSettingTime ? (
						<p className="px-4 font-bold text-lg m-0">
							{occurrence?.startDate
								? moment(occurrence?.startDate).format("YYYY年M月D日（ddd）")
								: "ー"}
						</p>
					) : (
						<p className="px-4 font-bold text-lg m-0">
							{occurrence?.startAt
								? moment(occurrence.startAt).format("YYYY年M月D日（ddd）")
								: "ー"}
						</p>
					)}

					<p className="px-4 m-0 mt-1">
						{category?.isSettingTime ? (
							<span
								className="inline-block rounded-full text-white px-2 font-bold"
								style={{
									backgroundColor: publicSettings?.PRIMARY_COLOR,
								}}
							>
								{`${
									category?.startDate
										? moment(category?.startDate).format("HH:mm")
										: "ー"
								}
                ～
                ${
									category?.endDate
										? moment(category?.endDate).format("HH:mm")
										: "ー"
								}`}
							</span>
						) : (
							<span
								className="inline-block rounded-full text-white px-2 font-bold"
								style={{
									backgroundColor: publicSettings?.PRIMARY_COLOR,
								}}
							>
								{`${
									occurrence?.startAt
										? moment(occurrence.startAt).format("HH:mm")
										: "ー"
								}
                ～
                ${
									occurrence?.endAt
										? moment(occurrence.endAt).format("HH:mm")
										: "ー"
								}`}
							</span>
						)}
					</p>
				</div>
			</motion.div>
			<motion.div
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				className="mb-8"
			>
				{contextHolderModalWarningEmpty}
				<Form
					form={reservationForm}
					onFinish={reservationHandler}
					size="large"
					layout="horizontal"
					requiredMark={false}
					colon={false}
					scrollToFirstError
				>
					<div className="mb-8">
						<p
							className="px-4 py-2 text-center text-lg font-bold mt-8 mb-4 rounded"
							style={{
								backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
								color: publicSettings?.PRIMARY_COLOR,
							}}
						>
							メッセージ
						</p>
						<div className="px-4">
							<Form.Item name="memo">
								<TextArea
									autoSize
									placeholder="追記事項やご質問がございましたら、ご入力ください。"
								/>
							</Form.Item>
						</div>

						{messages.map((item, index) => {
							return item?.type === "text" ? (
								<div className="px-4" key={`text-${index}`}>
									<StyleFormItem
										name={`label${item?.categoryMessageId}`}
										label={
											<span className="font-bold">{item.label}</span>
										}
										rules={[
											{
												required: item?.required,
												message: `${item.label} は必須です。`,
											},
										]}
									>
										<TextArea autoSize placeholder={item.label} />
									</StyleFormItem>
								</div>
							) : (
								<div className="px-4" key={`checkbox-${index}`}>
									<StyleFormItem
										name={`label${item?.categoryMessageId}`}
										label={
											<span className="font-bold">{item.label}</span>
										}
										rules={[
											{
												required: item?.required,
												message: `${item.label} は必須です。`,
											},
										]}
									>
										<Checkbox.Group>
											{item?.option?.map((i, idx) => (
												<Checkbox
													key={`option-${idx}`}
													value={i?.contents?.trim()}
												>
													{i?.contents?.trim()}
												</Checkbox>
											))}
										</Checkbox.Group>
									</StyleFormItem>
								</div>
							);
						})}
					</div>

					{category?.groupBooking || occasion?.groupBooking ? (
						<GroupBookingForm
							primaryColor={publicSettings?.PRIMARY_COLOR}
							primaryLightColor={publicSettings?.PRIMARY_LIGHT_COLOR}
							maxReservationsPerAccount={category?.maxReservationsPerAccount}
						/>
					) : null}

					<div className="flex flex-col mb-8 px-4">
						<TapAnimationComponent
							className="w-full"
							disabled={reserveMutation.isLoading}
						>
							<Button
								type="primary"
								className="w-full h-12"
								size="large"
								htmlType="submit"
								loading={reserveMutation.isLoading}
								disabled={reserveMutation.isLoading}
							>
								この内容で予約する
							</Button>
						</TapAnimationComponent>
						<div className="flex justify-center mt-4">
							<TapAnimationComponent>
								<Link
									to={`${
										COMMONS.CLIENT_CATEGORIES_ROUTE
									}/${liffId}/${categoryId}/${occasionId}?isProgram=${searchParams.get(
										"isProgram",
									)}`}
								>
									<Button className="w-32" size="large">
										戻る
									</Button>
								</Link>
							</TapAnimationComponent>
						</div>
					</div>
				</Form>
			</motion.div>
		</motion.div>
	);
};

export default ReservationMember;
